<template>
  <div
    class="state-item"
    :class="isWarning"
  >
    <div class="title">
      <strong class="company">{{data.companyName}}</strong>
      <div class="service">
        <img
          v-for="(icon, index) in filteredIcons"
          :key="index"
          :src="icon.src"
          :alt="icon.alt"
        />
      </div>
    </div>
    <div class="tag">
      <status-tag
        v-for="(tag, index) in filteredStatusTags"
        :key="index"
        :color="getTagColor(status)"
        size="small"
        :text="tag.text"
      />
      <font-awesome-icon
        v-if="data.options.tasks?.SENT_MAIL_EXTEND || data.options.tasks?.SENT_REMIND_MAIL"
        :icon="`fa-solid fa-envelope${data.options.tasks?.CONFIRMED_REPLY ? '-circle-check': ''}`"
        size="lg" 
        :style="{ color: data.options.tasks?.SENT_MAIL_EXTEND ? '#C11574' : '#5925DC' }"
      />
    </div>
    <div class="date">
      <span class="date-key">{{getDateLabel(status)}}</span>
      <span class="date-value">{{ data.date }}</span>
    </div>
    <a
      @click.prevent="navigateWithData(data.requestId, data.subId, status)"
      class="link"
    >
      <span class="blind">자세히</span>
    </a>
  </div>
</template>

<script setup lang='ts'>
import { defineProps, computed } from 'vue'
import { useRouter } from 'vue-router';
import { useRequestStore } from '@/stores/request';
import { getTagColor } from '@/utils'
import { getPaymentTypeValue, getPaymentCycleTypeValue, getDocumentTypeValue }  from '@/utils/getValueForKey'
import { RequestOnBoard } from '@/types/Request'
import moment from 'moment';
import StatusTag from '@/components/StatusTag.vue'
import IconBeusGray from '@/assets/images/ico-beus-g.svg'
import IconBiGray from '@/assets/images/ico-bi-g.svg'
import IconPVGray from '@/assets/images/ico-pv-g.svg'
import IconJamGray from '@/assets/images/ico-jam-g.svg'
import IconServiceGray from '@/assets/images/ico-service-g.svg'

interface Props {
  status: string,
  data: RequestOnBoard
}

const props = defineProps<Props>();

const router = useRouter();
const requestStore = useRequestStore();

const navigateWithData = (requestId: string, subId: string, status: string) => {
  requestStore.setRequestIds(requestId, subId);
  router.push(`/request/viewStat/${requestId}/${status}`);
};

const arrDisplayPaymentType = [
  'CREATED_REQUEST_PLAN', 
  'CREATED_REQUEST_PV',
  'CREATED_REQUEST_JAM',
  'CREATED_REQUEST_ADDITIONAL',
  'DAY_PAYMENT_WIRE',
  'DAY_PAYMENT_WIRE_DELAY',
  'DAY_PAYMENT_CARD',
  'NEED_TO_EXTEND'
]

const arrDisplayDocumentType = [
  'DAY_INVOICE_TAX', 
  'DAY_INVOICE'
]

const icons = [
  {
    condition: () => props.data.options.requestType === 'PLAN' && props.data.options.beusPlanNumber,
    src: IconBeusGray,
    alt: '플랜-히트맵',
  },
  {
    condition: () => props.data.options.requestType === 'PLAN' && props.data.options.baPlanNumber,
    src: IconBiGray,
    alt: '플랜-저니맵',
  },
  {
    condition: () => props.data.options.requestType === 'PV_CHARGE',
    src: IconPVGray,
    alt: 'PV 충전',
  },
  {
    condition: () => props.data.options.requestType === 'JAM_CHARGE',
    src: IconJamGray,
    alt: 'JAM 충전',
  },
  {
    condition: () => props.data.options.requestType === 'ADDITIONAL',
    src: IconServiceGray,
    alt: '부가서비스',
  },
];

const filteredIcons = computed(() =>
  icons.filter((icon) => icon.condition())
);

const statusTags = computed(() => [
  { // 결제 유형
    condition: arrDisplayPaymentType.includes(props.status),
    text: getPaymentTypeValue(props.data.options.paymentType),
  },
  { // 사용 기간 (N개월)
    condition: props.data.options.period && arrDisplayPaymentType.includes(props.status),
    text: `${props.data.options.period}개월`,
  },
  { // 결제 주기
    condition: props.data.options.paymentCycle && !arrDisplayDocumentType.includes(props.status),
    text: props.data.options.paymentCycle ? getPaymentCycleTypeValue(props.data.options.paymentCycle): '',
  },
  { // 자동 갱신 여부
    condition: props.data.options.recurring && arrDisplayPaymentType.includes(props.status),
    text: "자동갱신",
  },
  { // 서류명
    condition: arrDisplayDocumentType.includes(props.status),
    text: props.data.options.docType ? getDocumentTypeValue(props.data.options.docType): ''
  },
]);

const filteredStatusTags = computed(() => {
  return statusTags.value.filter(tag => tag.condition);
});

const getDateLabel = (status: string) => {
  if (getTagColor(status) === 'blue') return '사용 시작일';
  if (getTagColor(status) === 'purple') return '결제일';
  if (getTagColor(status) === 'pink') return '발행일';
  if (getTagColor(status) === 'rose') return '플랜 종료일';
}

const isWarning = computed(() => { 
  const today = moment().format('YYYY-MM-DD');
  const dataDate = moment(props.data.date).format('YYYY-MM-DD');

  return props.status === 'DAY_PAYMENT_WIRE_DELAY' || dataDate < today
    ? 'is-warning'
    : false;
});
</script>